import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  getCountFromServer,
  query,
  doc,
  setDoc,
  serverTimestamp,
  runTransaction,
  arrayUnion,
  updateDoc,
  orderBy,
} from 'firebase/firestore';
import { db } from '@festpix/services';

export interface CreateWalletParams {
  ownerEmail: string;
  ownerName: string;
  initialBalance?: number;
}

export async function createWallet(params: CreateWalletParams) {
  const { ownerEmail, ownerName, initialBalance = 0 } = params;

  // Create wallet
  const walletRef = await addDoc(collection(db, 'wallets'), {
    balance: initialBalance,
    ownerId: '',
    qrCode: '',
    associatedWallets: [],
  });

  // Generate QR code data
  const qrCodeData = JSON.stringify({ walletId: walletRef.id });

  // Update wallet with QR code
  await setDoc(doc(db, 'wallets', walletRef.id), {
    balance: initialBalance,
    ownerId: '',
    qrCode: qrCodeData,
    associatedWallets: [],
  });

  // Create user document
  const userRef = doc(db, 'users', walletRef.id);
  await setDoc(userRef, {
    email: ownerEmail,
    name: ownerName,
    role: 'attendee',
    walletId: walletRef.id,
  });

  return {
    walletId: walletRef.id,
    qrCodeData,
  };
}

export interface CreateChildWalletParams {
  parentWalletId: string;
  childName: string;
}

export async function adminCreateChildWallet(params: CreateChildWalletParams) {
  const { parentWalletId, childName } = params;

  // Verify parent wallet exists
  const parentWalletRef = doc(db, 'wallets', parentWalletId);
  const parentWalletDoc = await getDoc(parentWalletRef);

  if (!parentWalletDoc.exists()) {
    throw new Error('Carteira pai não encontrada');
  }

  // Create child wallet
  const childWalletRef = await addDoc(collection(db, 'wallets'), {
    balance: 0,
    ownerId: parentWalletDoc.data().ownerId || '',
    qrCode: '',
    parentWalletId: parentWalletId,
    childName: childName,
    isChildWallet: true,
    associatedWallets: [],
    createdAt: serverTimestamp(),
  });

  // Generate QR code data
  const qrCodeData = JSON.stringify({ walletId: childWalletRef.id });

  // Update child wallet with QR code
  await updateDoc(childWalletRef, {
    qrCode: qrCodeData,
  });

  // Update parent wallet's associatedWallets array
  await updateDoc(parentWalletRef, {
    associatedWallets: arrayUnion(childWalletRef.id),
  });

  return {
    walletId: childWalletRef.id,
    qrCodeData,
    childName,
    parentWalletId,
  };
}

export interface WalletData {
  id: string;
  balance: number;
  name: string;
  email: string;
  qrCode?: string;
  isChildWallet: boolean;
  childName?: string;
  parentWalletId?: string;
  parentWalletName?: string;
  associatedWallets: string[];
}

export interface WalletsResult {
  wallets: WalletData[];
  total: number;
}

// Build user lookup map for efficient name/email resolution
async function buildUserLookupMap(): Promise<Map<string, { name: string; email: string }>> {
  const usersSnapshot = await getDocs(collection(db, 'users'));
  const userMap = new Map<string, { name: string; email: string }>();

  usersSnapshot.docs.forEach((userDoc) => {
    const userData = userDoc.data();
    if (userData.walletId) {
      userMap.set(userData.walletId, {
        name: userData.name || 'Unknown',
        email: userData.email || '',
      });
    }
  });

  return userMap;
}

/**
 * Get all wallets with user information enriched.
 * Returns all wallets sorted by balance (desc).
 * Client-side handles pagination, filtering, and search.
 */
export async function getAllWallets(): Promise<WalletsResult> {
  // Build user lookup map first (single query instead of N+1)
  const userMap = await buildUserLookupMap();

  // Fetch all wallets ordered by balance
  const walletsSnapshot = await getDocs(
    query(collection(db, 'wallets'), orderBy('balance', 'desc'))
  );

  const wallets: WalletData[] = [];

  for (const walletDoc of walletsSnapshot.docs) {
    const walletData = walletDoc.data();

    // Skip booth wallets - they are managed in BoothWalletsPage
    if (walletData.type === 'booth' || walletData.ownerId === 'booth') {
      continue;
    }

    const isChildWallet = walletData.isChildWallet || false;

    let userName = 'Unknown';
    let userEmail = '';
    let parentWalletName = '';

    if (isChildWallet) {
      userName = walletData.childName || 'Carteira Filho';

      // Get parent wallet info using the map
      if (walletData.parentWalletId) {
        const parentUserInfo = userMap.get(walletData.parentWalletId);
        if (parentUserInfo) {
          parentWalletName = parentUserInfo.name;
        }
      }
    } else {
      // Get user info from the pre-built map
      const userInfo = userMap.get(walletDoc.id);
      if (userInfo) {
        userName = userInfo.name;
        userEmail = userInfo.email;
      }
    }

    wallets.push({
      id: walletDoc.id,
      balance: walletData.balance,
      name: userName,
      email: userEmail,
      qrCode: walletData.qrCode,
      isChildWallet: isChildWallet,
      childName: walletData.childName,
      parentWalletId: walletData.parentWalletId,
      parentWalletName: parentWalletName,
      associatedWallets: walletData.associatedWallets || [],
    });
  }

  return {
    wallets,
    total: wallets.length,
  };
}

/**
 * Get wallet counts for display
 */
export async function getWalletCounts(): Promise<{ total: number; parents: number; children: number }> {
  const walletsSnapshot = await getDocs(collection(db, 'wallets'));

  let total = 0;
  let parents = 0;
  let children = 0;

  walletsSnapshot.docs.forEach((walletDoc) => {
    const data = walletDoc.data();
    // Skip booth wallets
    if (data.type === 'booth' || data.ownerId === 'booth') {
      return;
    }
    total++;
    if (data.isChildWallet) {
      children++;
    } else {
      parents++;
    }
  });

  return { total, parents, children };
}

export interface BoothWalletData {
  id: string;
  boothId: string;
  boothName: string;
  balance: number;
  qrCode?: string;
}

export async function getBoothWallets(): Promise<BoothWalletData[]> {
  // Get all booths to map wallet IDs to booth names
  const boothsSnapshot = await getDocs(collection(db, 'booths'));
  const boothMap = new Map<string, { id: string; name: string }>();

  boothsSnapshot.docs.forEach((boothDoc) => {
    const boothData = boothDoc.data();
    if (boothData.mainWalletId) {
      boothMap.set(boothData.mainWalletId, {
        id: boothDoc.id,
        name: boothData.name || 'Estande sem nome',
      });
    }
  });

  // Get all booth wallets
  const walletsSnapshot = await getDocs(collection(db, 'wallets'));
  const boothWallets: BoothWalletData[] = [];

  for (const walletDoc of walletsSnapshot.docs) {
    const walletData = walletDoc.data();

    // Only include booth wallets
    if (walletData.type === 'booth' || walletData.ownerId === 'booth') {
      const boothInfo = boothMap.get(walletDoc.id);

      boothWallets.push({
        id: walletDoc.id,
        boothId: boothInfo?.id || '',
        boothName: boothInfo?.name || walletData.boothName || 'Estande desconhecido',
        balance: walletData.balance || 0,
        qrCode: walletData.qrCode,
      });
    }
  }

  return boothWallets;
}

export interface TransactionData {
  id: string;
  timestamp: any;
  type: string;
  amount: number;
  fromWalletId: string;
  toWalletId: string;
  boothName?: string;
  boothId?: string;
  performedBy?: {
    id: string;
    email: string;
    name?: string;
  };
  notes?: string;
}

export interface TransactionsResult {
  transactions: TransactionData[];
  total: number;
}

/**
 * Get all transactions sorted by timestamp (desc).
 * Client-side handles pagination, filtering, and search.
 */
export async function getTransactions(): Promise<TransactionsResult> {
  const transactionsSnapshot = await getDocs(
    query(collection(db, 'transactions'), orderBy('timestamp', 'desc'))
  );

  const transactions: TransactionData[] = transactionsSnapshot.docs.map((docSnapshot) => ({
    id: docSnapshot.id,
    ...docSnapshot.data(),
  })) as TransactionData[];

  return {
    transactions,
    total: transactions.length,
  };
}

/**
 * Get transaction count
 */
export async function getTransactionCount(): Promise<number> {
  const snapshot = await getCountFromServer(collection(db, 'transactions'));
  return snapshot.data().count;
}

export interface ManualRechargeParams {
  walletId: string;
  amount: number;
  adminUserId: string;
  adminEmail: string;
  notes?: string;
}

export async function manualRechargeWallet(params: ManualRechargeParams) {
  const { walletId, amount, adminUserId, adminEmail, notes } = params;

  if (amount <= 0) {
    throw new Error('O valor deve ser maior que zero');
  }

  const walletRef = doc(db, 'wallets', walletId);

  return runTransaction(db, async (transaction) => {
    const walletDoc = await transaction.get(walletRef);

    if (!walletDoc.exists()) {
      throw new Error('Carteira não encontrada');
    }

    const currentBalance = walletDoc.data().balance || 0;
    const newBalance = currentBalance + amount;

    // Update wallet balance
    transaction.update(walletRef, { balance: newBalance });

    // Create transaction record
    const transactionRef = doc(collection(db, 'transactions'));
    transaction.set(transactionRef, {
      type: 'manual_recharge',
      amount: amount,
      toWalletId: walletId,
      fromWalletId: null,
      timestamp: serverTimestamp(),
      performedBy: {
        id: adminUserId,
        email: adminEmail,
      },
      notes: notes || 'Recarga manual via admin',
      previousBalance: currentBalance,
      newBalance: newBalance,
    });

    return {
      transactionId: transactionRef.id,
      previousBalance: currentBalance,
      newBalance: newBalance,
    };
  });
}
